import { Box, Button, Checkbox, Flex, Icon, Popover, Portal, Text, VStack } from "@chakra-ui/react";
import { CaretDownIcon } from "@phosphor-icons/react/dist/ssr";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import { Tooltip } from "@/components/ui/tooltip";
import { cardData } from "@/hooks/useBuscadorHook";
import { buildUrlSearchParams, EMPTY_STATE, sameValueSet } from "@/pages/Buscar/useSearchState";

import { useSearchPage } from "../SearchPageContext";

const ALL_TYPE_KEYS = cardData.map((cat) => cat.key);

const committedToDraft = (stateTypes) => {
    if (!stateTypes || stateTypes.length === 0) return new Set(ALL_TYPE_KEYS);
    const draft = new Set();
    for (const cat of cardData) {
        if (cat.types.every((t) => stateTypes.includes(t))) draft.add(cat.key);
    }
    return draft;
};

const draftToCommitted = (draftSet) => {
    const result = [];
    for (const cat of cardData) {
        if (draftSet.has(cat.key)) result.push(...cat.types);
    }
    return result;
};

const isDraftAllSelected = (draftSet) => draftSet.size === ALL_TYPE_KEYS.length;
const isDraftEmpty = (draftSet) => draftSet.size === 0;

const pillLabel = (stateTypes) => {
    if (!stateTypes || stateTypes.length === 0) return "Tipo de propiedad";
    const activeCats = cardData.filter((cat) => cat.types.some((t) => stateTypes.includes(t)));
    if (activeCats.length === 0) return "Tipo (ninguno)";
    if (activeCats.length === 1) return activeCats[0].label;
    if (activeCats.length === cardData.length) return "Tipo de propiedad";
    return `Tipo (${activeCats.length})`;
};

export const usePropertyTypeDraft = (state) => {
    const [draft, setDraft] = useState(() => committedToDraft(state.type));
    // Dedicated-page identity (null on /Buscar). On a property-type page the
    // page's preset counts as the default selection, and committing a
    // different single category NAVIGATES to that category's sibling page
    // instead of rewriting filters under the wrong page identity. The
    // navigation logic lives here in the hook because two parents instantiate
    // it (the lg+ popover and the mobile drawer) and both must behave alike.
    const searchPage = useSearchPage();
    const defaults = searchPage?.DEFAULTS ?? EMPTY_STATE;
    const typeConfig =
        searchPage?.config?.kind === "type" &&
        searchPage.config.typeCategory &&
        searchPage.config.siblings
            ? searchPage.config
            : null;
    const router = useRouter();

    useEffect(() => {
        setDraft(committedToDraft(state.type));
    }, [state.type]);

    const draftAllSelected = isDraftAllSelected(draft);
    const draftEmpty = isDraftEmpty(draft);

    const committedAsDraft = committedToDraft(state.type);
    const draftDiffersFromCommitted =
        draft.size !== committedAsDraft.size ||
        Array.from(draft).some((k) => !committedAsDraft.has(k));
    const isDirty = !draftEmpty && draftDiffersFromCommitted;

    // "Active" means "deviates from the PAGE default" — a type page's own
    // preset does not light the pill or bump the drawer badge, mirroring how
    // Limpiar treats page defaults.
    const typeActive = !sameValueSet(state.type, defaults.type);

    const toggleCategory = (key) => {
        setDraft((d) => {
            const next = new Set(d);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleAll = () => {
        setDraft(draftAllSelected ? new Set() : new Set(ALL_TYPE_KEYS));
    };

    const commit = (setFilter, clearFilter) => {
        if (typeConfig) {
            const categories = Array.from(draft);
            const own = typeConfig.typeCategory;
            if (categories.length === 1 && categories[0] === own) {
                // Same category as the page: normal commit — it equals the
                // preset, so the URL stays bare.
                setFilter("type", draftToCommitted(draft));
                return;
            }
            if (categories.length === 1 && typeConfig.siblings[categories[0]]) {
                // Exactly one different category with a sibling landing page:
                // navigate there (legacy behavior; other filters are dropped,
                // as the legacy pages dropped them).
                router.push(typeConfig.siblings[categories[0]]);
                return;
            }
            // Anything not representable under a single type page (multiple
            // categories, "all", or a category without a sibling page, like
            // offices) falls through to the general search with the selection
            // and the user's other filters carried in the URL. All-selected
            // means no type filter at all, so the parameter strips.
            const committed = draftAllSelected ? [] : draftToCommitted(draft);
            const params = buildUrlSearchParams(
                { ...state, type: committed, polygons: [] },
                EMPTY_STATE
            );
            router.push(`/Buscar?${params.toString()}`);
            return;
        }
        if (draftAllSelected) clearFilter("type");
        else setFilter("type", draftToCommitted(draft));
    };

    const reset = (_setFilter, clearFilter) => {
        // clearFilter reverts to the page default (the preset on a type
        // page, everything on /Buscar); mirror it in the draft.
        setDraft(defaults.type?.length ? committedToDraft(defaults.type) : new Set(ALL_TYPE_KEYS));
        clearFilter("type");
    };

    return {
        draft,
        toggleCategory,
        toggleAll,
        draftAllSelected,
        draftEmpty,
        isDirty,
        typeActive,
        // Can't commit an empty draft — drawer's global Aplicar will disable
        // itself with a tooltip when this is false.
        canCommit: !draftEmpty,
        commit,
        reset,
    };
};

export const PropertyTypePopoverBody = ({ draft }) => (
    <Box>
        <Flex justify="flex-end" mb={2}>
            <Button onClick={draft.toggleAll} variant="ghost" size="xs" color="gray.500">
                {draft.draftAllSelected ? "Deseleccionar todos" : "Seleccionar todos"}
            </Button>
        </Flex>
        <VStack gap={2} align="stretch">
            {cardData.map((cat) => {
                const checked = draft.draft.has(cat.key);
                return (
                    <Box
                        key={cat.key}
                        as="button"
                        type="button"
                        onClick={() => draft.toggleCategory(cat.key)}
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                        w="100%"
                        px={2}
                        py={2}
                        borderRadius="md"
                        _hover={{ bg: "gray.50" }}
                        cursor="pointer"
                    >
                        <Checkbox.Root checked={checked} pointerEvents="none" mr={3}>
                            <Checkbox.HiddenInput />
                            <Checkbox.Control />
                        </Checkbox.Root>
                        <Text fontSize="sm" color="gray.800">
                            {cat.label}
                        </Text>
                    </Box>
                );
            })}
        </VStack>
    </Box>
);

const PropertyTypePopover = ({ state, setFilter, clearFilter }) => {
    const [open, setOpen] = useState(false);
    const draft = usePropertyTypeDraft(state);
    const pillActive = draft.typeActive;
    const label = pillLabel(state.type);

    const apply = () => {
        draft.commit(setFilter, clearFilter);
        setOpen(false);
    };
    const clear = () => {
        draft.reset(setFilter, clearFilter);
        setOpen(false);
    };

    return (
        <Popover.Root open={open} onOpenChange={(e) => setOpen(e.open)}>
            <Popover.Trigger asChild>
                <Button
                    h="40px"
                    minW="160px"
                    px={4}
                    fontSize="sm"
                    fontWeight={500}
                    bg={pillActive ? "colorPalette.solid" : "white"}
                    color={pillActive ? "white" : "gray.700"}
                    borderWidth="1px"
                    borderColor={pillActive ? "colorPalette.solid" : "gray.200"}
                    borderRadius="md"
                    _hover={{ bg: pillActive ? "colorPalette.solid" : "gray.50" }}
                >
                    {label}
                    <Icon ml={2} boxSize={3} opacity={0.7}>
                        <CaretDownIcon weight="bold" />
                    </Icon>
                </Button>
            </Popover.Trigger>
            <Portal>
                <Popover.Positioner>
                    <Popover.Content colorPalette="accent" w="320px" shadow="lg">
                        <Popover.Body p={4}>
                            <Text fontWeight={600} mb={2}>
                                Tipo de propiedad{draft.isDirty ? " •" : ""}
                            </Text>
                            <PropertyTypePopoverBody draft={draft} />
                            <Flex
                                justify="space-between"
                                gap={2}
                                borderTopWidth="1px"
                                borderColor="gray.100"
                                pt={3}
                                mt={3}
                            >
                                <Button
                                    onClick={clear}
                                    variant="ghost"
                                    size="sm"
                                    px={5}
                                    borderRadius="md"
                                    color="gray.600"
                                >
                                    Limpiar
                                </Button>
                                <Tooltip
                                    content="Selecciona al menos un tipo de propiedad para aplicar."
                                    disabled={draft.canCommit}
                                    openDelay={150}
                                >
                                    <Box>
                                        <Button
                                            onClick={apply}
                                            disabled={!draft.canCommit}
                                            size="sm"
                                            px={6}
                                            borderRadius="md"
                                            bg="colorPalette.solid"
                                            color="white"
                                            _hover={{ bg: "colorPalette.emphasized" }}
                                            _disabled={{
                                                bg: "gray.300",
                                                color: "gray.500",
                                                cursor: "not-allowed",
                                            }}
                                        >
                                            Aplicar
                                        </Button>
                                    </Box>
                                </Tooltip>
                            </Flex>
                        </Popover.Body>
                    </Popover.Content>
                </Popover.Positioner>
            </Portal>
        </Popover.Root>
    );
};

export default PropertyTypePopover;
